// src/components/common/Header.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { categories } from '../../data/categories';
import { supportPrograms } from '../../data/supportPrograms';
import Icon from './Icon';

const Header: React.FC = () => {
    const [term, setTerm] = useState('');
    const [mobileSearch, setMobileSearch] = useState(false);
    const [drawer, setDrawer] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // 이동하면 모두 닫기
    useEffect(() => {
        setMobileSearch(false);
        setDrawer(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (mobileSearch) inputRef.current?.focus();
    }, [mobileSearch]);

    // 드로어 열려 있을 때 배경 스크롤 잠금 + ESC 닫기
    useEffect(() => {
        if (!drawer) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setDrawer(false);
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('keydown', onKey);
        };
    }, [drawer]);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        const q = term.trim();
        if (!q) return;
        navigate(`/search?q=${encodeURIComponent(q)}`);
        setMobileSearch(false);
    };

    return (
        <>
            <header className="gh-header">
                <div className="gh-header__inner">
                    <button
                        type="button"
                        className="gh-header__menu"
                        onClick={() => setDrawer(true)}
                        aria-label="전체 메뉴 열기"
                        aria-expanded={drawer}
                    >
                        <Icon name="menu" size={22} />
                    </button>

                    <Link to="/" className="gh-logo" aria-label="고브인포 홈으로 이동">
                        <span className="gh-logo__mark">
                            <Icon name="shield" size={20} strokeWidth={1.9} />
                        </span>
                        <span className="gh-logo__text">
                            <span className="gh-logo__name">고브인포</span>
                            <span className="gh-logo__sub">정부지원금 정보센터</span>
                        </span>
                    </Link>

                    <div className={`gh-search${mobileSearch ? ' gh-search--open' : ''}`}>
                        <form className="gh-search__form" onSubmit={submit} role="search">
                            <label htmlFor="gh-search-input" className="sr-only">
                                정부지원금 검색
                            </label>
                            <input
                                id="gh-search-input"
                                ref={inputRef}
                                className="gh-search__input"
                                type="search"
                                value={term}
                                onChange={e => setTerm(e.target.value)}
                                placeholder="찾는 지원금을 검색하세요 (예: 근로장려금, 청년월세)"
                                autoComplete="off"
                            />
                            <button className="gh-search__btn" type="submit" aria-label="검색">
                                <Icon name="search" size={17} strokeWidth={2} />
                            </button>
                        </form>
                    </div>

                    <nav className="gh-util" aria-label="주요 안내">
                        <button
                            type="button"
                            className="gh-util__icon"
                            onClick={() => setMobileSearch(v => !v)}
                            aria-label={mobileSearch ? '검색 닫기' : '검색 열기'}
                            aria-expanded={mobileSearch}
                        >
                            <Icon name={mobileSearch ? 'close' : 'search'} size={20} />
                        </button>

                        <Link to="/2026" className="gh-util__link">
                            <Icon name="sparkle" size={16} />
                            <span>2026년 달라진 점</span>
                        </Link>
                        <Link to="/2027" className="gh-util__link gh-util__link--accent">
                            <Icon name="chart" size={16} />
                            <span>2027년 전망</span>
                        </Link>
                    </nav>
                </div>
            </header>

            {/* 모바일 전체 메뉴 */}
            {drawer && (
                <div className="gh-drawer" role="dialog" aria-modal="true" aria-label="전체 메뉴">
                    <button
                        type="button"
                        className="gh-drawer__scrim"
                        onClick={() => setDrawer(false)}
                        aria-label="메뉴 닫기"
                    />
                    <div className="gh-drawer__panel">
                        <div className="gh-drawer__head">
                            <span className="gh-drawer__title">전체 카테고리</span>
                            <button
                                type="button"
                                className="gh-drawer__close"
                                onClick={() => setDrawer(false)}
                                aria-label="메뉴 닫기"
                            >
                                <Icon name="close" size={20} />
                            </button>
                        </div>

                        <nav className="gh-drawer__body">
                            {categories.map(c => {
                                const count =
                                    c.id === 'all'
                                        ? supportPrograms.length
                                        : supportPrograms.filter(p => p.category === c.id).length;
                                return (
                                    <NavLink
                                        key={c.id}
                                        to={c.path}
                                        end={c.path === '/'}
                                        className={({ isActive }) =>
                                            `gh-drawer__item${isActive ? ' is-active' : ''}`
                                        }
                                    >
                                        <span className="gh-drawer__icon">
                                            <Icon name={c.icon} size={19} />
                                        </span>
                                        <span className="gh-drawer__text">
                                            <span className="gh-drawer__name">{c.name}</span>
                                            <span className="gh-drawer__desc">{c.tagline}</span>
                                        </span>
                                        <em className="gh-drawer__count">{count}</em>
                                    </NavLink>
                                );
                            })}

                            <div className="gh-drawer__sep" />

                            <Link to="/2026" className="gh-drawer__item">
                                <span className="gh-drawer__icon">
                                    <Icon name="sparkle" size={19} />
                                </span>
                                <span className="gh-drawer__text">
                                    <span className="gh-drawer__name">2026년 달라지는 정부지원금</span>
                                    <span className="gh-drawer__desc">기준 중위소득·최저임금 등 확정 기준</span>
                                </span>
                            </Link>
                            <Link to="/2027" className="gh-drawer__item">
                                <span className="gh-drawer__icon">
                                    <Icon name="chart" size={19} />
                                </span>
                                <span className="gh-drawer__text">
                                    <span className="gh-drawer__name">2027년 정부지원금 전망</span>
                                    <span className="gh-drawer__desc">예산안 820.9조원 기준 신설·확대</span>
                                </span>
                            </Link>
                            <Link to="/search" className="gh-drawer__item">
                                <span className="gh-drawer__icon">
                                    <Icon name="search" size={19} />
                                </span>
                                <span className="gh-drawer__text">
                                    <span className="gh-drawer__name">지원금 통합 검색</span>
                                    <span className="gh-drawer__desc">
                                        전체 {supportPrograms.length}건에서 찾기
                                    </span>
                                </span>
                            </Link>
                        </nav>

                        <div className="gh-drawer__foot">
                            <Link to="/privacy">개인정보처리방침</Link>
                            <Link to="/terms">이용약관</Link>
                            <Link to="/contact">문의하기</Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
